import {useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {pdfjs} from 'react-pdf'
import Tesseract from 'tesseract.js'
import {ROUTES} from '../../../../../_gori/constants'
import {extractAddresses, extractBarcodeNumber} from '../../utils/ocrUtils'
import {isValidGoriSignatureHash} from '../../utils/validation'
import {clearLabelSessionStorage} from '../../utils/sessionStorageUtils'
import {MAX_FILE_SIZE, MAX_STORAGE_SIZE} from '../../consts/storage'

export const useFileProcessor = (
  hash: string | undefined,
  workerRef: React.MutableRefObject<Tesseract.Worker | null>,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  isMountedRef: React.MutableRefObject<boolean>,
  setIsReviewing: (value: boolean) => void,
  processingAbortedRef?: React.MutableRefObject<boolean>
) => {
  const navigate = useNavigate()
  const intl = useIntl()

  const validateFileType = useCallback(async (file: File): Promise<boolean> => {
    const validMagicNumbers: Record<string, number[]> = {
      png: [0x89, 0x50, 0x4e, 0x47],
      jpeg: [0xff, 0xd8, 0xff],
      pdf: [0x25, 0x50, 0x44, 0x46],
      heic: [0x66, 0x74, 0x79, 0x70],
      webp: [0x52, 0x49, 0x46, 0x46],
    }

    try {
      const buffer = await file.slice(0, 12).arrayBuffer()
      const bytes = new Uint8Array(buffer)

      for (const [type, magic] of Object.entries(validMagicNumbers)) {
        if (magic.every((byte, i) => bytes[i] === byte)) {
          if (type === 'heic') {
            const heicBytes = bytes.slice(4, 12)
            const heicCheck = new TextDecoder('ascii', {fatal: true}).decode(heicBytes)
            const validHeicTypes = ['heic', 'heix', 'hevc', 'mif1']
            return validHeicTypes.some((t) => heicCheck.startsWith(t))
          }
          return true
        }
      }
    } catch {
      return false
    }

    return false
  }, [])

  const processCanvas = useCallback(
    async (canvas: HTMLCanvasElement) => {
      if (!isMountedRef.current || !hash || !isValidGoriSignatureHash(hash)) return false
      if (processingAbortedRef?.current) return false

      const ctx = canvas.getContext('2d')
      if (!ctx) return false

      const addressResult = await extractAddresses(canvas, workerRef.current)
      if (!addressResult.success || processingAbortedRef?.current) {
        toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_OCR_TIMEOUT'}))
        navigate(`${ROUTES.GORI_SIGNATURE.BASE}/${hash}`, {
          state: {from: 'upload', ocrFailed: true},
        })
        return false
      }

      const barcodeResult = await extractBarcodeNumber(canvas, workerRef.current)
      if (!barcodeResult.success || processingAbortedRef?.current) {
        toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_OCR_TIMEOUT'}))
        navigate(`${ROUTES.GORI_SIGNATURE.BASE}/${hash}`, {
          state: {from: 'upload', ocrFailed: true},
        })
        return false
      }

      sessionStorage.setItem(
        `label_ocr_${hash}`,
        JSON.stringify({
          from_street1: addressResult.from,
          to_street1: addressResult.to,
          barcode_number: barcodeResult.barcode,
          regions: {
            to: addressResult.regions.to,
            tracking: barcodeResult.region,
          },
        })
      )

      canvas.toBlob((blob) => {
        if (!isMountedRef.current || processingAbortedRef?.current) return

        if (blob) {
          const reader = new FileReader()
          reader.onloadend = () => {
            if (!isMountedRef.current || processingAbortedRef?.current) return

            const imageBase64 = reader.result as string
            const sizeInBytes = Math.ceil((imageBase64.length * 3) / 4)

            if (sizeInBytes > MAX_STORAGE_SIZE) {
              toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_IMAGE_TOO_LARGE'}))
              setIsReviewing(false)
              return
            }

            try {
              sessionStorage.setItem(`label_image_${hash}`, imageBase64)
              navigate(`${ROUTES.GORI_SIGNATURE.BASE}/${hash}`, {state: {from: 'upload'}})
            } catch (e) {
              if (hash) clearLabelSessionStorage(hash)

              if (e instanceof DOMException && e.name === 'QuotaExceededError') {
                toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_STORAGE_QUOTA_EXCEEDED'}))
              } else {
                toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_STORAGE_FULL'}))
              }
              setIsReviewing(false)
            }
          }
          reader.onerror = () => {
            if (!isMountedRef.current || processingAbortedRef?.current) return
            toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_FAILED_TO_PROCESS_IMAGE'}))
            setIsReviewing(false)
          }
          reader.readAsDataURL(blob)
        } else {
          navigate(`${ROUTES.GORI_SIGNATURE.BASE}/${hash}`, {state: {from: 'upload'}})
        }
      }, 'image/jpeg')

      return true
    },
    [navigate, hash, intl, isMountedRef, workerRef, setIsReviewing, processingAbortedRef]
  )

  const processImage = useCallback(
    async (file: File) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image()
        const reader = new FileReader()

        const cleanup = () => {
          reader.onload = null
          reader.onerror = null
          img.onload = null
          img.onerror = null
          img.src = ''
        }

        reader.onerror = () => {
          cleanup()
          reject(new Error('FAILED_TO_READ_FILE'))
        }

        reader.onload = async (e) => {
          if (!isMountedRef.current) {
            cleanup()
            return
          }

          img.onerror = () => {
            cleanup()
            reject(new Error('FAILED_TO_LOAD_IMAGE'))
          }

          img.onload = async () => {
            if (!isMountedRef.current) {
              cleanup()
              return
            }

            const canvas = canvasRef.current
            if (!canvas) {
              cleanup()
              return
            }

            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            if (!ctx) {
              cleanup()
              return
            }

            ctx.drawImage(img, 0, 0)
            cleanup()

            try {
              await processCanvas(canvas)
              resolve()
            } catch (error) {
              reject(error)
            }
          }

          img.src = e.target?.result as string
        }

        reader.readAsDataURL(file)
      })
    },
    [processCanvas, canvasRef, isMountedRef]
  )

  const processPDF = useCallback(
    async (file: File) => {
      const arrayBuffer = await file.arrayBuffer()
      const loadingTask = pdfjs.getDocument({data: arrayBuffer})
      const pdf = await loadingTask.promise

      if (!isMountedRef.current) return

      const page = await pdf.getPage(1)

      if (!isMountedRef.current) return

      const viewport = page.getViewport({scale: 2.0})
      const canvas = canvasRef.current
      if (!canvas) return

      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      await page.render({
        canvasContext: ctx,
        viewport: viewport,
      }).promise

      if (!isMountedRef.current) return

      await processCanvas(canvas)
    },
    [processCanvas, canvasRef, isMountedRef]
  )

  const handleFile = useCallback(
    async (file: File) => {
      const validTypes = [
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/heic',
        'image/heif',
        'image/webp',
        'application/pdf',
      ]

      const validExtensions = ['.png', '.jpg', '.jpeg', '.heic', '.heif', '.webp', '.pdf']
      const hasValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))

      if (!validTypes.includes(file.type) && !hasValidExtension) {
        toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_INVALID_FILE_TYPE'}))
        return
      }

      const isValidFile = await validateFileType(file)
      if (!isValidFile) {
        toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_INVALID_FILE_FORMAT'}))
        return
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(
          intl.formatMessage(
            {id: 'GORI_SIGNATURE_FILE_SIZE_EXCEEDED'},
            {size: MAX_FILE_SIZE / 1024 / 1024}
          )
        )
        return
      }

      setIsReviewing(true)
      try {
        if (file.type === 'application/pdf') {
          await processPDF(file)
        } else {
          await processImage(file)
        }
      } catch {
        toast.error(intl.formatMessage({id: 'GORI_SIGNATURE_FAILED_TO_PROCESS_FILE'}))
        setIsReviewing(false)
      }
    },
    [processPDF, processImage, intl, setIsReviewing, validateFileType]
  )

  return {handleFile}
}
